import { useEffect, useMemo, useState } from 'react';
import {
  DeleteOutlined,
  EditOutlined,
  LogoutOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { MenuInfo } from 'rc-menu/lib/interface';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useNotificationContext } from '@/features/notifications';
import { WorkspaceTabs } from '@/pages/workspace/WorkspaceTabs';
import {
  WorkspaceResponse,
  useGetAiAssistant,
  useGetWorkspaces,
  useProcCreate,
} from '@/shared/api';
import {
  useRouterContext,
  RouterPrivateContext,
  RouterSelectedCategory,
} from '@/shared/router';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { Row, ContextMenu } from '@/shared/ui';
import { getRandomAdjective } from '@/shared/utils';
import { KnowledgeBase } from '@/widgets/knowledge-base/KnowledgeBase';
import { PageLayout } from '@/widgets/page-layout';
import LogoWhite from '@assets/full-logo-white.svg?react';
import { CreateProcUsingAiModal } from './CreateProcUsingAiModal';
import {
  DeleteWorkspaceModal,
  LeaveWorkspaceModal,
  MyInstances,
  SidebarLeft,
  SimpleCreateProcModal,
  RenameWorkspaceModal,
} from '../workspace';

enum WorkspaceMenuOptions {
  Rename = 'rename',
  Leave = 'leave',
  Delete = 'delete',
}

const categoryTitleMap: Record<string, string> = {
  knowledgeBase: 'Knowledge Base',
  myInstances: 'My Instances',
  myProcs: 'My Procs',
};

const { Title } = Typography;

const getCategoryFromPathname = (pathname: string) => {
  if (pathname.includes('my-instances')) {
    return 'myInstances';
  }
  if (pathname.includes('knowledge-base')) {
    return 'knowledgeBase';
  }
  return 'myProcs';
};

export const Dashboard = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotificationContext();
  const { theme } = useThemeContext();
  const { activeOrganization, setDashboardGoBackLink } = useRouterContext();

  const [selectedCategory, setSelectedCategory] =
    useState<RouterSelectedCategory>(
      getCategoryFromPathname(location.pathname),
    );
  const [selectedWorkspace, setSelectedWorkspace] =
    useState<WorkspaceResponse>();

  const [createProcUsingAiModalOpen, setCreateProcUsingAiModalOpen] =
    useState(false);
  const [simpleCreateProcModalOpen, setSimpleCreateProcModalOpen] =
    useState(false);
  const [renameWorkspaceModalOpen, setRenameWorkspaceModalOpen] =
    useState(false);
  const [leaveWorkspaceModalOpen, setLeaveWorkspaceModalOpen] = useState(false);
  const [deleteWorkspaceModalOpen, setDeleteWorkspaceModalOpen] =
    useState(false);

  const { data: workspacesData, isLoading: workspacesDataLoading } =
    useGetWorkspaces<WorkspaceResponse[]>(
      {},
      {
        onSuccess(data) {
          if (selectedWorkspace) {
            const updatedWorkspace = data.find(
              (workspace: WorkspaceResponse) =>
                workspace.id === selectedWorkspace.id,
            );
            if (updatedWorkspace) {
              setSelectedWorkspace(updatedWorkspace);
            } else {
              setSelectedWorkspace(data[0]);
            }
          }
        },
        select: ({ data }) => data,
      },
    );
  const hasSeveralWorkspaces = Number(workspacesData?.length) > 1;

  const { data: assistantData } = useGetAiAssistant(
    { pathParams: { id: activeOrganization?.id as string } },
    { enabled: Boolean(activeOrganization?.id) },
  );

  const { mutate: createProc } = useProcCreate();

  const onContextMenuClick = (e: MenuInfo) => {
    switch (e.key) {
      case WorkspaceMenuOptions.Rename:
        setRenameWorkspaceModalOpen(true);
        break;
      case WorkspaceMenuOptions.Leave:
        setLeaveWorkspaceModalOpen(true);
        break;
      case WorkspaceMenuOptions.Delete:
        setDeleteWorkspaceModalOpen(true);
        break;
    }
  };

  const options: MenuProps['items'] = useMemo(
    () =>
      [
        {
          label: (
            <Space>
              <EditOutlined />
              Rename
            </Space>
          ),
          key: WorkspaceMenuOptions.Rename,
        },
        activeOrganization?.role !== 'admin' && {
          label: (
            <Space>
              <LogoutOutlined />
              Leave
            </Space>
          ),
          key: WorkspaceMenuOptions.Leave,
        },
        hasSeveralWorkspaces && {
          type: 'divider',
        },
        hasSeveralWorkspaces && {
          label: (
            <Space>
              <DeleteOutlined />
              Delete
            </Space>
          ),
          key: WorkspaceMenuOptions.Delete,
          style: {
            color: themeConfig[theme].colors.error,
          },
        },
      ].filter(Boolean) as MenuProps['items'],
    [activeOrganization?.role, hasSeveralWorkspaces, theme],
  );

  const isWorkspaceSelected = useMemo(
    () => selectedCategory === 'myProcs',
    [selectedCategory],
  );

  const isAiEnabled = useMemo(
    () =>
      !(!assistantData?.id || Number(assistantData?.aiFreeQueriesCount) <= 0),
    [assistantData],
  );

  const content = useMemo(() => {
    switch (selectedCategory) {
      case 'myProcs':
        return <WorkspaceTabs />;
      case 'myInstances':
        return <MyInstances />;
      case 'knowledgeBase':
        return <KnowledgeBase isReadOnly={false} />;
      default:
        return null;
    }
  }, [selectedCategory]);

  const handleCreateProcManually = () => {
    createProc(
      {
        body: {
          title: `My ${getRandomAdjective()} new proc`,
          workspaceId: selectedWorkspace?.id || workspacesData?.[0]?.id || '',
        },
      },
      {
        onSuccess(data) {
          queryClient.invalidateQueries([
            'dashboardProcsGetPaginated',
            'myProcs',
            selectedWorkspace?.id,
          ]);
          navigate(`/dashboard/author/proc/${data.id}`, {
            state: { procId: data.id },
          });
        },
        onError: (error) => {
          showNotification({
            type: 'error',
            message: 'Create proc error',
            description: error.message,
          });
        },
      },
    );
  };

  useEffect(() => {
    // Update selectedCategory whenever location changes
    setSelectedCategory(getCategoryFromPathname(location.pathname));
    if (
      location.pathname.includes('/dashboard/workspaces/') ||
      location.pathname.includes('/dashboard/knowledge-base') ||
      location.pathname.includes('/dashboard/my-instances')
    ) {
      setDashboardGoBackLink(location.pathname);
    }
  }, [location.pathname, setDashboardGoBackLink]);

  return (
    <RouterPrivateContext.Provider
      value={{
        selectedCategory,
        setSelectedCategory,
        selectedWorkspace,
        setSelectedWorkspace,
      }}
    >
      {!workspacesDataLoading && (
        <PageLayout
          headerContent={
            <>
              {theme === 'light' ? (
                <img
                  alt="wordmark.svg"
                  src="https://suresteps-public.s3.amazonaws.com/wordmark.svg"
                  style={{ height: 25 }}
                />
              ) : (
                <LogoWrapper>
                  <LogoWhite />
                </LogoWrapper>
              )}
            </>
          }
          contentStyle={{
            paddingRight: 16,
            background: themeConfig[theme].colors.bgBase,
          }}
          sidebarLeftContent={<SidebarLeft />}
          sidebarLeftWidth={'auto'}
        >
          <Row
            justifyContent="space-between"
            alignItems="center"
            style={{ marginBottom: 30, marginTop: -13 }}
          >
            <Row>
              <Title
                data-testid="workspace-title"
                level={4}
                style={{ marginBottom: 0 }}
              >
                {selectedWorkspace?.title
                  ? selectedWorkspace?.title
                  : categoryTitleMap[selectedCategory!]}
              </Title>
              {isWorkspaceSelected && (
                <div
                  data-testid="workspace-context-menu-button"
                  style={{ marginTop: 2 }}
                >
                  <ContextMenu onClick={onContextMenuClick} options={options} />
                </div>
              )}
            </Row>
            <Space>
              <Button
                size="large"
                data-testid="header-manual-create-proc-button"
                onClick={handleCreateProcManually}
                style={{ width: 'fit-content' }}
              >
                <Space>
                  <EditOutlined />
                  Manual Create
                </Space>
              </Button>
              {isAiEnabled && (
                <Button
                  size="large"
                  data-testid="header-ai-create-proc-button"
                  type="primary"
                  onClick={() => setCreateProcUsingAiModalOpen(true)}
                  style={{ width: 'fit-content', fontWeight: 600 }}
                >
                  <Space>
                    <RobotOutlined />
                    AI Create
                  </Space>
                </Button>
              )}
            </Space>
          </Row>
          <div>{content}</div>
        </PageLayout>
      )}
      <CreateProcUsingAiModal
        open={createProcUsingAiModalOpen}
        handleCloseModal={(open) => setCreateProcUsingAiModalOpen(open)}
      />
      <SimpleCreateProcModal
        open={simpleCreateProcModalOpen}
        onCancel={() => setSimpleCreateProcModalOpen(false)}
      />
      <RenameWorkspaceModal
        open={renameWorkspaceModalOpen}
        onCancel={() => setRenameWorkspaceModalOpen(false)}
      />
      <LeaveWorkspaceModal
        open={leaveWorkspaceModalOpen}
        onCancel={() => setLeaveWorkspaceModalOpen(false)}
      />

      {deleteWorkspaceModalOpen && (
        <DeleteWorkspaceModal
          open={deleteWorkspaceModalOpen}
          onCancel={() => setDeleteWorkspaceModalOpen(false)}
        />
      )}
    </RouterPrivateContext.Provider>
  );
};

const LogoWrapper = styled.div`
  display: flex;
  svg {
    height: 25px;
    width: auto;
  }
`;
